import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
    Button,
    CircularProgress,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import AllOrdersGrid from "../components/Shipping/AllOrdersGrid.jsx";
import GridFilterBar, { useGridFilter } from "../components/Helpers/GridFilterBar.jsx";
import PullDateRangeFields from "../components/Helpers/PullDateRangeFields.jsx";
import { ORDER_FILTER_FIELDS } from "../components/Shipping/orderFilterFields.js";
import TabNavigation from "../components/TabNavigation.jsx";
import {
    catalogQuietButtonSx,
    catalogSearchFieldSx,
} from "../components/Products/catalogGridStyles.js";
import useApiData from "../services/useApiData.js";
import PostData from "../services/PostData.js";

function orderSearchHaystack(order) {
    const f = order?.fields || {};
    return [
        f["Order Number"],
        f["Walmart Customer Order ID"],
        f["Selling channel"],
        f["Order Status"],
        f["Platform Status"],
        f["Item ID"],
        f["SKU Helper"],
        f["Item Title"],
        f["Buyer Name"],
        f["Ship to city"],
        f["Ship to province/region/state"],
        f["Ship to zip"],
        f["Shipping Service"],
        f["Shipping Tracking Number"],
        f["Catalog Item IDs"],
    ]
        .join(" ")
        .toLowerCase();
}

const ORDER_TABS = [
    { label: "All Orders", value: 0, channel: null },
    { label: "eBay", value: 1, channel: "ebay" },
    { label: "Walmart", value: 2, channel: "walmart" },
];

function sellingChannel(order) {
    return String(order?.fields?.["Selling channel"] || "").toLowerCase();
}

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultPullRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 3);
    return { startDate: dayInputValue(start), endDate: dayInputValue(end) };
}

function pullRangeError(startDate, endDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        return "Choose a start and end date.";
    }
    if (startDate > endDate) return "Start date must be on or before the end date.";
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const days = Math.round((end.getTime() - start.getTime()) / 86400000);
    if (days > 62) return "Date range cannot be longer than 62 days.";
    return "";
}

export default function Orders() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { apply: applyGridFilter, barProps: orderFilterBar } = useGridFilter(ORDER_FILTER_FIELDS);
    const [value, setValue] = useState(() => {
        const tab = parseInt(searchParams.get("t"), 10);
        return ORDER_TABS.some((item) => item.value === tab) ? tab : 0;
    });
    const initialRange = defaultPullRange();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [pulling, setPulling] = useState(false);
    const [pullError, setPullError] = useState("");
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const [searchInput, setSearchInput] = useState("");
    const rangeError = pullRangeError(startDate, endDate);

    const orderNumber = searchParams.get("order") || "";
    const fromReturn = searchParams.get("fromReturn") || "";
    const fromShipment = searchParams.get("fromShipment") || "";
    const listUrl = orderNumber
        ? `api/broylesllc/orders?order=${encodeURIComponent(orderNumber)}`
        : "api/broylesllc/orders";
    const { data, error, isLoading, load, refetch } = useApiData(listUrl);
    const orders = Array.isArray(data) ? data : [];

    const handleChange = (_event, newValue) => {
        setValue(newValue);
        setSearchParams({ t: String(newValue) });
    };

    const clearOrderFilter = () => {
        setSearchParams(fromReturn ? { fromReturn } : {});
    };

    const pullOrders = async (action) => {
        if (rangeError) return;
        setMenuAnchor(null);
        setPulling(true);
        setPullError("");
        try {
            const response = await PostData(
                { action, startDate, endDate, syncStatuses: true },
                "broylesllc/shipping/get-orders",
                null
            );
            if (!response?.success) {
                const message = response?.error?.response?.data?.message;
                setPullError(message || "Could not pull orders for that date range.");
                return;
            }
            await refetch?.();
        } finally {
            setPulling(false);
        }
    };

    useEffect(() => {
        const raw = searchParams.get("t");
        const tabFromUrl = parseInt(raw, 10);
        const valid = ORDER_TABS.some((item) => item.value === tabFromUrl);
        if (valid) {
            if (tabFromUrl !== value) setValue(tabFromUrl);
            return;
        }
        if (value !== 0) setValue(0);
        if (raw !== "0") {
            const next = { t: "0" };
            if (searchParams.get("order")) next.order = searchParams.get("order");
            if (searchParams.get("fromReturn")) next.fromReturn = searchParams.get("fromReturn");
            if (searchParams.get("fromShipment")) next.fromShipment = searchParams.get("fromShipment");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams, value]);

    const activeTab = ORDER_TABS.find((item) => item.value === value) ?? ORDER_TABS[0];
    const visibleOrders = useMemo(() => {
        const tabbed = orderNumber
            ? orders
            : !activeTab.channel
              ? orders
              : orders.filter((order) => sellingChannel(order) === activeTab.channel);
        const filtered = applyGridFilter(tabbed);
        const q = searchInput.trim().toLowerCase();
        if (!q) return filtered;
        return filtered.filter((order) => orderSearchHaystack(order).includes(q));
    }, [orders, activeTab, orderNumber, applyGridFilter, searchInput]);

    return (
        <CatalogPageShell
            title="Orders"
            subtitle="All Orders — filter with field conditions, combined with and or or."
        >
            {error ? (
                <p style={{ color: "#b91c1c", padding: "8px 16px", margin: 0 }}>
                    Failed to load orders.
                </p>
            ) : null}
            <div className="app-page-stack">
                {orderNumber || fromReturn || fromShipment ? (
                    <div className="catalog-toolbar" style={{ borderBottom: "none" }}>
                        {fromReturn ? (
                            <Button
                                size="small"
                                component={RouterLink}
                                to="/returns"
                                startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
                                sx={catalogQuietButtonSx}
                            >
                                Back to returns
                            </Button>
                        ) : null}
                        {fromShipment ? (
                            <Button
                                size="small"
                                component={RouterLink}
                                to="/order-shipping"
                                startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
                                sx={catalogQuietButtonSx}
                            >
                                Back to shipping
                            </Button>
                        ) : null}
                        {orderNumber ? (
                            <span className="catalog-selection-hint">
                                Order {orderNumber}
                                {!load && !isLoading && orders.length === 0
                                    ? " is not in the orders list yet."
                                    : ""}
                            </span>
                        ) : null}
                        {orderNumber ? (
                            <Button size="small" onClick={clearOrderFilter} sx={catalogQuietButtonSx}>
                                Show all orders
                            </Button>
                        ) : null}
                    </div>
                ) : null}
                <div style={{ flexShrink: 0 }}>
                    <TabNavigation
                        value={value}
                        onChange={handleChange}
                        tabs={ORDER_TABS}
                    />
                </div>
                <GridFilterBar {...orderFilterBar} />
                <div className="catalog-toolbar" style={{ borderBottom: "none" }}>
                    <TextField
                        size="small"
                        placeholder="Search order, buyer, item, tracking…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div className="catalog-toolbar-spacer" />
                    {pullError ? (
                        <Typography component="span" sx={{ color: "#b91c1c", fontSize: 13 }}>
                            {pullError}
                        </Typography>
                    ) : (
                        <span className="catalog-selection-hint">
                            {visibleOrders.length} order{visibleOrders.length === 1 ? "" : "s"}
                            {searchInput.trim() ? " (filtered)" : ""}
                        </span>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={pulling}
                        startIcon={
                            pulling ? (
                                <CircularProgress size={14} />
                            ) : (
                                <SyncOutlinedIcon fontSize="small" />
                            )
                        }
                        endIcon={<KeyboardArrowDownOutlinedIcon fontSize="small" />}
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        sx={catalogQuietButtonSx}
                    >
                        Pull orders
                    </Button>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        slotProps={{ paper: { sx: { minWidth: 300, px: 0.5, py: 0.5 } } }}
                    >
                        <PullDateRangeFields
                            startDate={startDate}
                            endDate={endDate}
                            onStartChange={setStartDate}
                            onEndChange={setEndDate}
                            error={rangeError}
                            hint="Updates Platform Status and keeps Bread Bskt order status."
                        />
                        <MenuItem disabled={Boolean(rangeError) || pulling} onClick={() => pullOrders("all")}>
                            All channels
                        </MenuItem>
                        <MenuItem disabled={Boolean(rangeError) || pulling} onClick={() => pullOrders("ebay")}>
                            eBay
                        </MenuItem>
                        <MenuItem disabled={Boolean(rangeError) || pulling} onClick={() => pullOrders("walmart")}>
                            Walmart
                        </MenuItem>
                    </Menu>
                </div>
                <AllOrdersGrid
                    orders={visibleOrders}
                    loading={Boolean(load || isLoading || pulling)}
                />
            </div>
        </CatalogPageShell>
    );
}
